import { resetViewAccess, resetWriteAccess, setViewAccess, setWriteAccess } from '@Reducers/AccessGroup';

import { ActionIdsType, actionListEntity } from '@Reducers/AccessGroup/Type';

import { checkAllowedActions } from '@Reducers/AccessGroup/case';

import { AppDispatch, RootStateType } from '@Store';

import { Box, Switch, Tooltip, Typography } from '@mui/material';

import { alpha, styled } from '@mui/material/styles';

import React from 'react';

import { useDispatch, useSelector } from 'react-redux';

export function Actions({
  data,
  pageId,
  isAllViewTrue,
}: {
  data: actionListEntity;
  pageId: string;
  isAllViewTrue: boolean;
}) {
  const dispatch = useDispatch<AppDispatch>();

  const { actionsIds } = useSelector((state: RootStateType) => state.accessGroup);

  // View Access
  const isViewAccessEnabled = actionsIds?.some((item: ActionIdsType) => {
    const { isReadOnly } = checkAllowedActions(item?.selectedActions);

    return item?._id === data?._id && isReadOnly;
  });

  // Edit Access
  const isWriteAccessEnabled = actionsIds?.some((item: ActionIdsType) => {
    const { isWriteOnly } = checkAllowedActions(item?.selectedActions);

    return item?._id === data?._id && isWriteOnly;
  });

  const handleViewChange = () => {
    if (isViewAccessEnabled) {
      dispatch(
        resetViewAccess({
          pageId: pageId,
          actionId: data?._id,
        }),
      );
    } else {
      dispatch(
        setViewAccess({
          actionId: data?._id,
        }),
      );
    }
  };

  const handleEditChange = () => {
    if (isWriteAccessEnabled) {
      dispatch(
        resetWriteAccess({
          pageId: pageId,
          actionId: data?._id,
        }),
      );
    } else {
      dispatch(
        setWriteAccess({
          pageId: pageId,
          actionId: data?._id,
        }),
      );
    }
  };

  return (
    <ActionContainer>
      {/* Name */}
      <Box flex={1}>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: 'text.secondary',
          }}
        >
          {data?.name}
        </Typography>
      </Box>

      {/* View */}
      <PermissionCol>
        {data?.isReadOnly ? (
          <Tooltip title={isViewAccessEnabled ? 'View Access Enabled' : 'Enable View Access'}>
            <span>
              <StyledSwitch checked={isViewAccessEnabled} onChange={handleViewChange} disabled={!isAllViewTrue} />
            </span>
          </Tooltip>
        ) : (
          <DisabledText>—</DisabledText>
        )}
      </PermissionCol>

      {/* Edit */}
      <PermissionCol>
        {data?.isWriteOnly ? (
          <Tooltip title={isWriteAccessEnabled ? 'Edit Access Enabled' : 'Enable Edit Access'}>
            <span>
              <StyledSwitch checked={isWriteAccessEnabled} onChange={handleEditChange} disabled={!isAllViewTrue} />
            </span>
          </Tooltip>
        ) : (
          <DisabledText>—</DisabledText>
        )}
      </PermissionCol>
    </ActionContainer>
  );
}

/* ---------------- STYLES ---------------- */

const ActionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',

  width: '100%',

  padding: '14px 24px 14px 84px',

  background: alpha(theme.palette.primary.main, 0.02),

  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,

  transition: 'all 0.25s ease',

  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
}));

const PermissionCol = styled(Box)({
  width: 120,

  display: 'flex',

  justifyContent: 'center',

  alignItems: 'center',
});

const DisabledText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,

  fontSize: '18px',

  fontWeight: 600,
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: theme.palette.primary.main,

    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  },

  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: theme.palette.primary.light,

    opacity: 1,
  },

  '& .MuiSwitch-track': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.3),
  },

  '& .MuiSwitch-switchBase': {
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  },
}));
